import React from "react";
import { connect } from "react-redux";
import { GridLayout } from "../../common/GridLayout";

import {
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    ColumnFixing,
    ColumnHeaderFilter,
    DataGrid,
    Export,
    FilterPanel,
    FilterRow,
    Grouping,
    GroupPanel,
    HeaderFilter,
    LoadPanel,
    Lookup,
    Paging,
    Scrolling,
    Sorting,
    StateStoring,
} from "devextreme-react/data-grid";

import {
    KIT_PM_USE_OPTIONS,
    KIT_STATUS_OPTIONS,
    PREVIEW_GRID_KEY,
} from "./RecalculateKitUsageConstants";

const previewGrid = React.createRef();
let accessToken = "";

/**
 * Preview grid for the IM "Recalculate Kit Usage Details" screen (legacy IMD0086).
 *
 * One row per kit DETAIL line flattened onto its parent kit header — the rowset the legacy
 * `TAbWhereBuilder.Preview` opened over `qryIMKitRecalculate`. Read-only: no <Editing>, no CustomStore,
 * nothing is posted back, so the DevExtreme inline-edit rules (Architecture.md 4-6, 14-16) do not apply.
 *
 * Field names are PascalCase because the operation endpoints serialise with DefaultContractResolver
 * (see KitUsageRecalculationController), unlike the camelCase report definition the page fetches.
 *
 * Kit Status and Kit PM Use render through <Lookup> rather than showing their raw code (rule 3), from the
 * same option arrays the criteria panel's Flag dropdowns are built from.
 *
 * All 35 columns of qryIMKitRecalculate are present, reproducing the legacy grid's own 26-shown /
 * 9-hidden split (see the comments over each block below, and KitUsageRecalculationPreviewDTO for how
 * that split was read out of the DFM). Hidden columns are hidden, not omitted, so the column chooser and
 * Export reach them exactly as the legacy hidden fields did on the desktop grid.
 *
 * Column ORDER is not the DFM's field order. The DFM declares the 15 user-defined columns before
 * KITTCODE, which would push the identifying columns off to the right; the identity columns lead here
 * instead. Users can reorder freely - allowColumnReordering is on and <StateStoring> persists it.
 *
 * SIZING IS THE HOST'S. This renders at height 100% and expects to be mounted inside the popup's
 * `.op-preview-grid` flex item (see RecalculateKitUsage.js and the shared operationScreen.css), which
 * is what gives 100% something to resolve against. The host also owns the "Records matching your
 * criteria" count below it - hence no pager here, since the two would report different numbers.
 */
class RecalculateKitUsageGridPreview extends React.Component {
    constructor(props) {
        super(props);
        accessToken = props.oidc.user.access_token;

        this.loadState = this.loadState.bind(this);
        this.saveState = this.saveState.bind(this);
    }

    loadState() {
        return GridLayout.sendStorageRequest(PREVIEW_GRID_KEY, "GET", accessToken);
    }

    saveState(state) {
        GridLayout.sendStorageRequest(PREVIEW_GRID_KEY, "POST", accessToken, state);
    }

    render() {
        return (
            // A plain div, NOT className="row". Bootstrap's .row carries -15px side margins meant to
            // cancel a container's padding; there is no such padding inside the popup, so it pulled the
            // grid 15px past each edge of the dialog.
            <div style={{ height: "100%", width: "100%" }}>
                <DataGrid
                    onExporting={(e) => {
                        if (window.handleDevExtremeDataGridExport) {
                            window.handleDevExtremeDataGridExport(e);
                        }
                    }}
                    dataSource={this.props.dataSource}
                    keyExpr="RowId"
                    ref={previewGrid}
                    showBorders={true}
                    className="customDataTableDesignCommit"
                    hoverStateEnabled={true}
                    allowColumnResizing={true}
                    allowColumnReordering={true}
                    columnAutoWidth={true}
                    showRowLines={true}
                    // The DevExtreme option, not a style rule: the widget has to know its own height to
                    // size the virtual scroller and to recompute on a window resize. The host wraps this
                    // in .op-preview-grid, a flex item with a definite height, so 100% resolves.
                    height="100%"
                    width="100%"
                >
                    {/*
                      One continuous list, scrolled - no pager. The dialog's footer reports how many
                      rows matched, and a pager under that count would contradict it: the user reads
                      "Records matching your criteria: 54" and then sees a grid claiming to hold 100 per
                      page. Delphi's TLookupForm has no pager either.

                      Paging is DISABLED rather than left at its default. Virtual scrolling on its own
                      does not turn it off - the grid keeps a page size and renders rows in pages behind
                      the scroll - so the pager component alone being gone is not enough.
                    */}
                    <Paging enabled={false} />
                    <ColumnChooser
                        enabled={true}
                        title="Choose Column"
                        width={290}
                        height={290}
                        sortOrder="asc"
                    >
                        <ColumnChooserSearch
                            enabled={true}
                            editorOptions={{ placeholder: "Search column" }}
                        />
                    </ColumnChooser>
                    <ColumnFixing enabled={true} />
                    <ColumnHeaderFilter />
                    <Export enabled={true} allowExportSelectedData={true} text="Export" />
                    <Sorting visible={true} mode="multiple" />
                    <LoadPanel enabled={true} />
                    <FilterRow visible={true} />
                    <FilterPanel visible={true} />
                    <HeaderFilter />
                    <GroupPanel visible={true} />
                    <Grouping autoExpandAll={false} />
                    <StateStoring
                        enabled={true}
                        type="custom"
                        customLoad={this.loadState}
                        storageKey={PREVIEW_GRID_KEY}
                        customSave={this.saveState}
                    />
                    {/* Technical key - hidden (rule 8). */}
                    <Column dataField="RowId" caption="Row Id" visible={false} />
                    <Column dataField="KitRecordNumber" caption="Kit Record #" dataType="string" />
                    <Column dataField="KitCode" caption="Kit Code" dataType="string" />
                    <Column dataField="KitDescription" caption="Kit Description" dataType="string" />
                    <Column dataField="KitStatus" caption="Kit Status" dataType="string">
                        <Lookup dataSource={KIT_STATUS_OPTIONS} displayExpr="Text" valueExpr="ID" />
                    </Column>
                    <Column dataField="KitPmUse" caption="Kit PM Use" dataType="string">
                        <Lookup dataSource={KIT_PM_USE_OPTIONS} displayExpr="Text" valueExpr="ID" />
                    </Column>
                    <Column dataField="ProductGroup" caption="Kit Product Group" dataType="string" />
                    <Column dataField="ProductionItem" caption="Kit Prod Item" dataType="string" />
                    <Column dataField="ProductionWarehouse" caption="Kit Prod WH" dataType="string" />
                    <Column dataField="InputItem" caption="Input Item" dataType="string" />
                    <Column dataField="InputWarehouse" caption="Input WH" dataType="string" />
                    <Column dataField="InputLot" caption="Input Lot" dataType="string" />
                    <Column dataField="Customer" caption="Customer" dataType="string" />

                    {/*
                      The detail line's user-defined blocks. All 15 are VisibleOnGrid = True in
                      uIMM0086.dfm, so they are visible columns, captioned with the DFM's own
                      DisplayLabels. dataType="date" matches the IM module's convention for
                      USERDEFINEDDATE columns (see common/lookups/Layouts/ImLayouts.js).
                    */}
                    <Column dataField="UserDefinedCode1" caption="Input UDF Code 1" dataType="string" />
                    <Column dataField="UserDefinedCode2" caption="Input UDF Code 2" dataType="string" />
                    <Column dataField="UserDefinedCode3" caption="Input UDF Code 3" dataType="string" />
                    <Column dataField="UserDefinedCode4" caption="Input UDF Code 4" dataType="string" />
                    <Column dataField="UserDefinedCode5" caption="Input UDF Code 5" dataType="string" />
                    <Column dataField="UserDefinedDate1" caption="Input UDF Date 1" dataType="date" />
                    <Column dataField="UserDefinedDate2" caption="Input UDF Date 2" dataType="date" />
                    <Column dataField="UserDefinedDate3" caption="Input UDF Date 3" dataType="date" />
                    <Column dataField="UserDefinedDate4" caption="Input UDF Date 4" dataType="date" />
                    <Column dataField="UserDefinedDate5" caption="Input UDF Date 5" dataType="date" />
                    <Column dataField="UserDefinedQuantity1" caption="Input UDF Qty 1" dataType="number" />
                    <Column dataField="UserDefinedQuantity2" caption="Input UDF Qty 2" dataType="number" />
                    <Column dataField="UserDefinedQuantity3" caption="Input UDF Qty 3" dataType="number" />
                    <Column dataField="UserDefinedQuantity4" caption="Input UDF Qty 4" dataType="number" />
                    <Column dataField="UserDefinedQuantity5" caption="Input UDF Qty 5" dataType="number" />

                    {/*
                      Columns the legacy grid carries but HIDES. Hidden here, not omitted, so the
                      column chooser and Export still reach them.

                      The legacy split was read out of uIMM0086.dfm: each of these is either a
                      TAbBytesField with Visible = False (and TAbBytesField.VisibleOnGrid defaults to
                      False), or the TAbMemoField COMMENT, whose constructor sets
                      FVisibleOnGrid := False. Everything above is VisibleOnGrid = True.
                    */}
                    <Column dataField="KitHeaderRowId" caption="Kit Header Row Id" dataType="string" visible={false} />
                    <Column dataField="InputItemRowId" caption="Input Item Row Id" dataType="string" visible={false} />
                    <Column dataField="InputWarehouseRowId" caption="Input WH Row Id" dataType="string" visible={false} />
                    <Column dataField="InputLotRowId" caption="Input Lot Row Id" dataType="string" visible={false} />
                    <Column dataField="CustomerRowId" caption="Customer Row Id" dataType="string" visible={false} />
                    <Column dataField="ProductGroupRowId" caption="Kit Product Group Row Id" dataType="string" visible={false} />
                    <Column dataField="ProductionItemRowId" caption="Kit Prod Item Row Id" dataType="string" visible={false} />
                    <Column dataField="ProductionWarehouseRowId" caption="Kit Prod WH Row Id" dataType="string" visible={false} />
                    <Column dataField="KitComment" caption="Kit Comment" dataType="string" visible={false} />
                </DataGrid>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(RecalculateKitUsageGridPreview);
